import { useContext, useEffect } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useUserContext } from 'contexts/UserContext';
import { useSectionsProgressContext } from 'contexts/SectionsProgressContext';
import { AvailableUnitContext } from 'contexts/AvailableUnitContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Hook to determine whether to show the AffordaPortal (new portal) or the old portal.
 *
 * The decision is based on the following criteria:
 * 1. Feature flags: The new portal must be enabled for the property
 * 2. User workflow: The user must be in the middle of a workflow (not completed all sections or not assigned a unit)
 * 3. User data:
 *    - Show new portal if user has no data at all (new user)
 *    - Show new portal if user has already started using the new portal (has completed steps)
 *    - Show old portal if user has data in old portal but not in new portal
 *
 * @returns {boolean} showAffordaPortal - Whether to show the AffordaPortal (true) or the old portal (false)
 */
const useShowAffordaPortal = () => {
  const { affordaPortaV1 } = useFlags();
  const { isAffordableProperty } = useContext(AppContext);
  const { userProfile } = useUserContext();
  const { allSectionsAreComplete, sectionsProgress, isInitialized } =
    useSectionsProgressContext();
  const { hasAssignedUnit } = useContext(AvailableUnitContext);

  // Check for old portal data (any existing user data in the old portal)
  const hasOldPortalData = Boolean(
    userProfile.applicationId ||
      userProfile.householdId ||
      userProfile.leaseId ||
      userProfile.unitId ||
      userProfile.isApplicant ||
      userProfile.isResident ||
      userProfile.isPriorResident ||
      userProfile.areFeesApplied ||
      userProfile.areFeesPaid,
  );

  // Check for new portal data (any completed steps in the new portal)
  const hasNewPortalData = Boolean(
    isInitialized &&
      sectionsProgress &&
      Object.keys(sectionsProgress).length > 0,
  );

  // Step 1: Check if the new portal feature is enabled
  const isNewPortalFeatureEnabled = isAffordableProperty && affordaPortaV1;

  // Step 2: Check if the user has completed all sections or has an assigned unit
  const userHasNotCompletedAllSectionsOrDoesNotHaveAnAssignedUnit =
    !allSectionsAreComplete || !hasAssignedUnit;

  // Step 3: Determine if we should use the new portal based on user data
  // - Show new portal if user has no data at all (new user)
  // - Show new portal if user has already started using the new portal
  // - Show old portal if user has data in old portal but not in new portal
  const shouldUseNewPortalBasedOnUserData =
    !hasOldPortalData || hasNewPortalData;

  // Final decision: All conditions must be true to show the new portal
  const showAffordaPortal =
    isNewPortalFeatureEnabled &&
    userHasNotCompletedAllSectionsOrDoesNotHaveAnAssignedUnit &&
    shouldUseNewPortalBasedOnUserData;

  // Log the portal selection decision to Datadog
  useEffect(() => {
    datadogRum.addAction('portal.should-afforda-portal.decision', {
      showAffordaPortal,
      isNewPortalFeatureEnabled,
      userHasNotCompletedAllSectionsOrDoesNotHaveAnAssignedUnit,
      shouldUseNewPortalBasedOnUserData,
      hasOldPortalData,
      hasNewPortalData,
      userProfile: {
        hasApplicationId: Boolean(userProfile.applicationId),
        hasHouseholdId: Boolean(userProfile.householdId),
        hasLeaseId: Boolean(userProfile.leaseId),
        hasUnitId: Boolean(userProfile.unitId),
        isApplicant: Boolean(userProfile.isApplicant),
        isResident: Boolean(userProfile.isResident),
        isPriorResident: Boolean(userProfile.isPriorResident),
        areFeesApplied: Boolean(userProfile.areFeesApplied),
        areFeesPaid: Boolean(userProfile.areFeesPaid),
      },
      featureFlags: {
        affordaPortaV1,
      },
    });
  }, [
    showAffordaPortal,
    isNewPortalFeatureEnabled,
    userHasNotCompletedAllSectionsOrDoesNotHaveAnAssignedUnit,
    shouldUseNewPortalBasedOnUserData,
    hasOldPortalData,
    hasNewPortalData,
    userProfile,
    affordaPortaV1,
  ]);

  return showAffordaPortal;
};

export default useShowAffordaPortal;
